import { For, createMemo, createEffect, createSignal, onCleanup, onMount } from 'solid-js';
import wm, { snapIconPosition } from '../lib/wmStore.js';
import Window from './Window.jsx';
import DesktopIcons from './DesktopIcons.jsx';
import ContextMenu from './ContextMenu.jsx';
import { homeMenu } from '../lib/homeMenuStore.js';
import { rendererFor } from '../widgets/index.jsx';

const EDITABLE_INPUT_TYPES = new Set([
  'email', 'number', 'password', 'search', 'tel', 'text', 'url',
]);
const WIDGET_DRAG_MIME = 'application/x-redfur-widget';

function closestElement(target) {
  if (target?.nodeType === 1) return target;
  return target?.parentElement || null;
}

function isNativeContextTarget(target) {
  const el = closestElement(target);
  if (!el) return false;
  if (el.closest('textarea, select, [contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"]')) return true;
  const input = el.closest('input');
  if (!input) return false;
  return EDITABLE_INPUT_TYPES.has((input.getAttribute('type') || 'text').toLowerCase());
}

function hasSelectedTextAtTarget(target) {
  if (typeof window === 'undefined') return false;
  const selection = window.getSelection?.();
  if (!selection || selection.isCollapsed || !selection.toString().trim()) return false;
  const el = closestElement(target);
  if (!el) return true;
  for (let i = 0; i < selection.rangeCount; i += 1) {
    try {
      if (selection.getRangeAt(i).intersectsNode(el)) return true;
    } catch { /* detached selection target */ }
  }
  return false;
}

function contextMenuPoint(e) {
  if (typeof e?.clientX === 'number' && typeof e?.clientY === 'number') {
    return { x: e.clientX, y: e.clientY };
  }
  const anchor = closestElement(e.target) || document.activeElement;
  const rect = anchor?.getBoundingClientRect?.();
  if (rect) return { x: rect.left + 18, y: rect.top + 18 };
  return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
}

function hasWidgetDragPayload(dt) {
  if (!dt?.types) return false;
  const types = Array.from(dt.types);
  return types.includes(WIDGET_DRAG_MIME) || types.includes('text/x-redfur-widget');
}

export default function Desktop() {
  const [defaultMenu, setDefaultMenu] = createSignal(null);
  const [dropGhost, setDropGhost] = createSignal(null);
  const dash = createMemo(() => wm.state.dashboards.find(d => d.id === wm.state.activeDashboardId));
  const widgets = createMemo(() => dash()?.widgets || []);
  let desktopRef;

  function defaultContextItems() {
    const hasWindows = widgets().length > 0;
    const hasIcons = wm.activeDesktopIcons().length > 0;
    const pt = defaultMenu() || { x: 80, y: 80 };
    const rect = desktopRef?.getBoundingClientRect?.() || { left: 0, top: 0 };
    return [
      {
        icon: homeMenu.open() ? '×' : '☰',
        label: homeMenu.open() ? 'Close home menu' : 'Open home menu',
        iconTone: 'theme',
        onSelect: () => homeMenu.open() ? homeMenu.close() : homeMenu.openWith('widgets'),
      },
      { icon: '◆', label: 'Theme controls', iconTone: 'theme', onSelect: () => homeMenu.openWith('theme') },
      { separator: true },
      {
        icon: '📁',
        label: 'New desktop folder',
        iconTone: 'theme',
        onSelect: () => wm.createDesktopFolder({
          x: pt.x - rect.left - 44,
          y: pt.y - rect.top - 44,
        }),
      },
      { icon: '📌', label: 'Tidy desktop shortcuts', disabled: !hasIcons, onSelect: () => wm.arrangeDesktopIcons() },
      { separator: true },
      { icon: '▦', label: 'Auto-organize windows', disabled: !hasWindows, onSelect: () => wm.arrangeTile() },
      { icon: '↗', label: 'Restore all windows', disabled: !hasWindows, onSelect: () => wm.restoreAll() },
    ];
  }

  function onDesktopDragOver(e) {
    if (!hasWidgetDragPayload(e.dataTransfer)) return;
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'copy'; } catch { /* noop */ }
    const rect = desktopRef?.getBoundingClientRect?.() || { left: 0, top: 0 };
    const rawX = e.clientX - rect.left - 44;
    const rawY = e.clientY - rect.top - 46;
    const snapped = snapIconPosition(rawX, rawY, wm.activeDesktopIcons());
    const cur = dropGhost();
    if (!cur || cur.x !== snapped.x || cur.y !== snapped.y) {
      setDropGhost({ x: snapped.x, y: snapped.y, label: 'Drop shortcut' });
    }
  }

  function onDesktopDragLeave(e) {
    if (!dropGhost()) return;
    const next = e.relatedTarget;
    if (next && desktopRef?.contains?.(next)) return;
    setDropGhost(null);
  }

  function onDesktopDrop(e) {
    if (!hasWidgetDragPayload(e.dataTransfer)) return;
    e.preventDefault();
    const ghost = dropGhost();
    setDropGhost(null);
    if (typeof document !== 'undefined') {
      document.body.classList.remove('v2-dragging-widget');
    }
    let payload = null;
    try {
      const raw = e.dataTransfer.getData(WIDGET_DRAG_MIME) || e.dataTransfer.getData('text/x-redfur-widget') || e.dataTransfer.getData('text/plain');
      payload = raw ? JSON.parse(raw) : null;
    } catch { /* ignore malformed drag payload */ }
    if (!payload?.type) return;

    const targetBubble = closestElement(e.target)?.closest?.('[data-folder-bubble-id]');
    if (targetBubble) {
      const folderId = targetBubble.getAttribute('data-folder-bubble-id');
      if (folderId) {
        wm.addShortcutToFolder(folderId, {
          widget_type: payload.type,
          title: payload.title,
          emoji: payload.emoji,
        });
        homeMenu.close();
        return;
      }
    }

    const targetIconEl = closestElement(e.target)?.closest?.('[data-icon-id]');
    if (targetIconEl) {
      const targetId = targetIconEl.getAttribute('data-icon-id');
      const targetKind = targetIconEl.getAttribute('data-icon-kind');
      if (targetId && targetKind === 'folder') {
        wm.addShortcutToFolder(targetId, {
          widget_type: payload.type,
          title: payload.title,
          emoji: payload.emoji,
        });
        homeMenu.close();
        return;
      }
      if (targetId && targetKind === 'shortcut') {
        const created = wm.addDesktopIcon({
          widget_type: payload.type,
          title: payload.title,
          emoji: payload.emoji,
        });
        if (created) {
          wm.mergeIconsIntoFolder(created.id, targetId);
        }
        homeMenu.close();
        return;
      }
    }

    const rect = desktopRef?.getBoundingClientRect?.() || { left: 0, top: 0 };
    const pos = ghost || snapIconPosition(e.clientX - rect.left - 44, e.clientY - rect.top - 46, wm.activeDesktopIcons());
    wm.addDesktopIcon({
      widget_type: payload.type,
      title: payload.title,
      emoji: payload.emoji,
      x: pos.x,
      y: pos.y,
    });
    homeMenu.close();
  }

  function onDocumentContextMenu(e) {
    if (e.defaultPrevented) return;
    if (isNativeContextTarget(e.target) || hasSelectedTextAtTarget(e.target)) {
      setDefaultMenu(null);
      return;
    }
    e.preventDefault();
    setDefaultMenu(contextMenuPoint(e));
  }

  onMount(() => {
    document.addEventListener('contextmenu', onDocumentContextMenu);
  });
  onCleanup(() => {
    document.removeEventListener('contextmenu', onDocumentContextMenu);
  });

  // #149 — when any visible (non-minimized) widget is maximized, mark
  // <body> so the home launcher and tabrail toggle dock onto the
  // window header bar instead of floating over content.
  createEffect(() => {
    if (typeof document === 'undefined') return;
    const hasMax = widgets().some(w => w.maximized && !w.minimized);
    document.body.classList.toggle('v2-has-max', hasMax);
  });
  onCleanup(() => {
    if (typeof document !== 'undefined') document.body.classList.remove('v2-has-max');
  });

  return (
    <main
      ref={el => desktopRef = el}
      class="v2-desktop"
      onDragOver={onDesktopDragOver}
      onDragLeave={onDesktopDragLeave}
      onDrop={onDesktopDrop}
    >
      <DesktopIcons dropGhost={dropGhost()} />
      <For each={widgets()}>
        {(w) => {
          const meta = wm.state.catalog.find(c => c.type === w.widget_type);
          const Body = rendererFor(w.widget_type); // always returns at least UnknownWidget
          const [titleChrome, setTitleChrome] = createSignal(null);
          return (
            <Window widget={w} meta={meta} titleChrome={titleChrome()}>
              <Body
                widget={w}
                settings={w.settings}
                maximized={!!w.maximized}
                identity={wm.state.viewer}
                setTitleChrome={setTitleChrome}
              />
            </Window>
          );
        }}
      </For>
      <ContextMenu
        open={!!defaultMenu()}
        x={defaultMenu()?.x}
        y={defaultMenu()?.y}
        label="App actions"
        items={defaultContextItems()}
        onClose={() => setDefaultMenu(null)}
      />
    </main>
  );
}
